/**
 * Admin → Tenant detail (Phase 35.2, super-admin only).
 *
 * Route: /admin/tenants/:tenantId
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  deleteTenant,
  getTenant,
  getTenantProvisioningStatus,
  rerunBootstrap,
  updateTenantStatus,
} from "../services/adminTenants.js";

// Mirrors `_PROTECTED_TENANT_IDS` in backend/functions/admin/app.py — the
// server is authoritative (returns 403), but hiding the button is friendlier
// UX. Update both in lockstep.
const PROTECTED_TENANT_IDS = new Set(["tenant_wiz"]);
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import BudgetPanel from "../components/admin/BudgetPanel.jsx";

const POLL_INTERVAL_MS = 3000;
// Newly-provisioned tenants race the SFN's CreateTenantRecord step. Retry
// 404s for up to 30s before surfacing the error.
const NOT_FOUND_RETRY_INTERVAL_MS = 2000;
const NOT_FOUND_RETRY_TIMEOUT_MS = 30000;

function Field({ label, value }) {
  return (
    <div className="detail-field">
      <dt className="detail-field__label">{label}</dt>
      <dd className="detail-field__value">{value ?? "—"}</dd>
    </div>
  );
}

export default function TenantDetail() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [provStatus, setProvStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const pushToast = useStore((s) => s.pushToast);
  const navigate = useNavigate();

  const load = useCallback(() => {
    return getTenant(tenantId)
      .then((data) => {
        setTenant(data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [tenantId]);

  // Newly-created tenants 404 until the provisioning Step Function's
  // CreateTenantRecord state completes. Retry quietly during that window
  // instead of leaving a stale error on screen.
  const retryStartRef = useRef(null);
  useEffect(() => {
    if (!error || tenant) return;
    if (!error.startsWith("404")) return;
    if (retryStartRef.current === null) retryStartRef.current = Date.now();
    if (Date.now() - retryStartRef.current > NOT_FOUND_RETRY_TIMEOUT_MS) return;
    const id = setTimeout(() => {
      getTenant(tenantId)
        .then((data) => {
          setTenant(data);
          setError(null);
          retryStartRef.current = null;
        })
        .catch((err) => setError(err.message));
    }, NOT_FOUND_RETRY_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [error, tenant, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll provisioning status while the tenant is still provisioning.
  const tenantStatus = tenant?.status;
  useEffect(() => {
    if (!tenantStatus || tenantStatus !== "provisioning") return;
    const id = setInterval(() => {
      getTenantProvisioningStatus(tenantId)
        .then((s) => {
          setProvStatus(s);
          if (s.status !== "provisioning") {
            setTenant((prev) => ({ ...prev, status: s.status }));
            clearInterval(id);
          }
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [tenantStatus, tenantId]);

  async function handleRerunBootstrap() {
    if (!tenant) return;
    setBootstrapping(true);
    try {
      await rerunBootstrap(tenantId);
      pushToast("Bootstrap started — research will complete in ~2–5 min.");
    } catch (err) {
      pushToast(`Error: ${err.message}`, "error");
    } finally {
      setBootstrapping(false);
    }
  }

  async function handleDelete() {
    if (!tenant) return;
    if (deleteConfirmText !== tenant.tenantId) return;
    setDeleting(true);
    try {
      await deleteTenant(tenantId);
      pushToast(`Tenant "${tenant.tenantId}" deleted.`);
      navigate("/admin/tenants");
    } catch (err) {
      pushToast(`Error: ${err.message}`, "error");
      setDeleting(false);
    }
  }

  async function handleStatusToggle() {
    if (!tenant) return;
    const next = tenant.status === "suspended" ? "active" : "suspended";
    setToggling(true);
    try {
      await updateTenantStatus(tenantId, next);
      setTenant((prev) => ({ ...prev, status: next }));
      pushToast(`Tenant ${next === "suspended" ? "suspended" : "reactivated"}`);
    } catch (err) {
      pushToast(`Error: ${err.message}`, "error");
    } finally {
      setToggling(false);
    }
  }

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorBubble message={error} />;
  if (!tenant) return null;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <Link to="/admin/tenants" className="admin-page__breadcrumb">
            ← Tenants
          </Link>
          <h1 className="admin-page__title">{tenant.name || tenant.tenantId}</h1>
        </div>
        <div className="admin-page__header-actions">
          {/* Super-admins are exempt from SetupRouteGuard's auto-redirect, so
              there's no nav cue pointing them at /setup for setup_review
              tenants. Surface an explicit affordance here. The link uses
              the active tenant context already on the request via the
              X-Tenant-Id header — the user will have selected this tenant
              via the switcher (or arrived here via a deep link that did so).
              JAM-199 — surfaced three times during validation before fix. */}
          {tenant.status === "setup_review" && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                // Set the viewed tenant as the active X-Tenant-Id BEFORE
                // navigating to /setup. Without this, the setup page calls
                // /api/tenant/setup with whatever tenant was previously
                // active — if that was an `active` tenant, TenantSetupPage's
                // own useEffect bounces back to "/" and the user lands on
                // the project kanban thinking the link is broken.
                // localStorage is the same key TenantSwitcher writes to,
                // and we hard-navigate so the new page mounts with the new
                // header in place from the very first request.
                try {
                  localStorage.setItem("aicoach.activeTenantId", tenant.tenantId);
                } catch {
                  // Private-browsing / quota — fall through to navigate
                  // anyway; the page will use the previous activeTenantId.
                }
                window.location.assign("/setup");
              }}
            >
              Open Setup
            </button>
          )}
          {tenant.status !== "provisioning" && tenant.status !== "deleted" && (
            <button
              className={`btn btn-sm ${
                tenant.status === "suspended" ? "btn btn-ghost" : "btn btn-destructive"
              }`}
              disabled={toggling}
              onClick={handleStatusToggle}
            >
              {tenant.status === "suspended" ? "Reactivate" : "Suspend"}
            </button>
          )}
          {/* v5.x soft-delete — flips status to "deleted" without
              cascading destructive cleanup. Hidden for the canonical
              Wiz tenant (server also refuses with 403) and for already-
              deleted tenants. Confirmation requires typing the tenant ID. */}
          {tenant.status !== "provisioning"
            && tenant.status !== "deleted"
            && !PROTECTED_TENANT_IDS.has(tenant.tenantId) && (
            <button
              className="btn btn-sm btn-destructive"
              disabled={deleting}
              onClick={() => {
                setDeleteConfirmText("");
                setShowDeleteModal(true);
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {tenant.status === "provisioning" && (
        <div className="provision-banner">
          <span className="provision-banner__spinner" aria-hidden="true" />
          Provisioning in progress
          {provStatus?.updatedAt && (
            <span className="provision-banner__time">
              {" "}· last updated {new Date(provStatus.updatedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      <dl className="detail-grid">
        <Field label="Tenant ID" value={tenant.tenantId} />
        <Field label="Status" value={tenant.status} />
        <Field label="Project KB ID" value={tenant.projectKbId || "—"} />
        <Field label="S3 Prefix" value={tenant.s3Prefix || "—"} />
        <Field
          label="Domains"
          value={(tenant.domains || []).join(", ") || "—"}
        />
        <Field
          label="Created"
          value={
            tenant.createdAt
              ? new Date(tenant.createdAt).toLocaleString()
              : "—"
          }
        />
        <Field
          label="Created by"
          value={tenant.createdBy || "—"}
        />
        <Field
          label="Updated"
          value={
            tenant.updatedAt
              ? new Date(tenant.updatedAt).toLocaleString()
              : "—"
          }
        />
      </dl>

      {tenant.tenantProfile?.research_metadata && (
        <BootstrapStatus
          profile={tenant.tenantProfile}
          onView={() => setShowProfileModal(true)}
          onRerun={handleRerunBootstrap}
          rerunning={bootstrapping}
        />
      )}

      {!tenant.tenantProfile && tenant.status === "active" && (
        <div className="admin-panel">
          <h3>Bootstrap Status</h3>
          <p className="admin-panel__muted">
            No tenantProfile yet. Click below to run the research pipeline
            for this tenant — it takes ~2–5 min and writes the profile to
            DynamoDB when complete.
          </p>
          <button
            className="btn btn-primary"
            onClick={handleRerunBootstrap}
            disabled={bootstrapping}
          >
            {bootstrapping ? "Starting…" : "Run bootstrap"}
          </button>
        </div>
      )}

      {showProfileModal && tenant.tenantProfile && (
        <ProfileModal
          profile={tenant.tenantProfile}
          onClose={() => setShowProfileModal(false)}
        />
      )}
      {showDeleteModal && (
        <DeleteTenantModal
          tenantId={tenant.tenantId}
          tenantName={tenant.name}
          confirmText={deleteConfirmText}
          onConfirmTextChange={setDeleteConfirmText}
          onConfirm={handleDelete}
          onClose={() => {
            if (!deleting) setShowDeleteModal(false);
          }}
          deleting={deleting}
        />
      )}
      {/* v5 Phase 49 — super-admin per-tenant budget. */}
      <BudgetPanel tenantId={tenantId} />
    </div>
  );
}

function DeleteTenantModal({
  tenantId,
  tenantName,
  confirmText,
  onConfirmTextChange,
  onConfirm,
  onClose,
  deleting,
}) {
  const matches = confirmText === tenantId;
  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog">
      <div
        className="modal-card delete-tenant-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-card__header">
          <h3>Delete tenant</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={deleting}
          >
            ×
          </button>
        </div>
        <div className="modal-card__body delete-tenant-modal__body">
          <p>
            This soft-deletes <strong>{tenantName || tenantId}</strong>. The
            tenant will be hidden from the switcher and admin list.
          </p>
          <p className="delete-tenant-modal__warning">
            Provisioned AWS resources (Knowledge Base, S3 prefixes, CloudWatch
            alarms, SNS subscriptions) are <em>not</em> torn down — they
            remain billable until the destructive cascade ships.
          </p>
          <label className="delete-tenant-modal__label">
            Type <code>{tenantId}</code> to confirm:
            <input
              type="text"
              className="delete-tenant-modal__input"
              value={confirmText}
              onChange={(e) => onConfirmTextChange(e.target.value)}
              placeholder={tenantId}
              autoFocus
              disabled={deleting}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <div className="delete-tenant-modal__actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-destructive"
              onClick={onConfirm}
              disabled={!matches || deleting}
            >
              {deleting ? "Deleting…" : "Delete tenant"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BootstrapStatus({ profile, onView, onRerun, rerunning }) {
  const meta = profile.research_metadata || {};
  return (
    <div className="admin-panel">
      <h3>Bootstrap Status</h3>
      <dl className="detail-grid">
        <Field label="Generated at" value={meta.generated_at ? new Date(meta.generated_at).toLocaleString() : "—"} />
        <Field label="Confidence" value={meta.confidence || "—"} />
        <Field label="Sources" value={(meta.sources || []).length} />
        {meta.error && <Field label="Error" value={meta.error} />}
      </dl>
      <div className="admin-panel__actions">
        <button className="btn btn-ghost" onClick={onView}>
          View profile
        </button>
        <button
          className="btn btn-secondary"
          onClick={onRerun}
          disabled={rerunning}
        >
          {rerunning ? "Starting…" : "Re-run bootstrap"}
        </button>
      </div>
    </div>
  );
}

// JAM-197 — structured view replaces the raw <pre>{JSON} dump that
// overflowed for long competitor / vocabulary lists. Tenant-admins use
// the proper /setup ProfileEditor; this modal is the super-admin debug
// surface only, so keep it lightweight: known fields rendered in
// sections, unknown fields collapsed under "Other", with a Raw JSON
// toggle for the original behaviour.
function ProfileSection({ title, children }) {
  return (
    <div className="profile-section">
      <h4 className="profile-section__title">{title}</h4>
      {children}
    </div>
  );
}

function KeyValue({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="profile-kv">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function ProfileModal({ profile, onClose }) {
  const [showRaw, setShowRaw] = useState(false);
  const competitors = Array.isArray(profile.competitors) ? profile.competitors : [];
  const vocabulary = Array.isArray(profile.vocabulary) ? profile.vocabulary : [];
  const personas = Array.isArray(profile.target_personas) ? profile.target_personas : [];
  const valueProps = Array.isArray(profile.value_props) ? profile.value_props : [];
  // Known top-level keys we render explicitly; remainder bucket-displays
  // under "Other" so a schema addition doesn't silently disappear.
  const known = new Set([
    "name", "domain", "industry", "description", "competitors",
    "vocabulary", "target_personas", "value_props",
    "schemaVersion", "research_metadata", "sales_methodology",
  ]);
  const other = Object.fromEntries(
    Object.entries(profile).filter(([k]) => !known.has(k))
  );
  const meta = profile.research_metadata || {};

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog">
      <div className="modal-card modal-card-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-card__header">
          <h3>{profile.name || "Tenant Profile"}</h3>
          <div className="modal-card__header-actions">
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? "Structured view" : "View raw JSON"}
            </button>
            <button className="modal-card__close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        {showRaw ? (
          <pre className="modal-card__body">{JSON.stringify(profile, null, 2)}</pre>
        ) : (
          <div className="modal-card__body">
            <ProfileSection title="Identity">
              <dl className="profile-kv-list">
                <KeyValue label="Name" value={profile.name} />
                <KeyValue label="Domain" value={profile.domain} />
                <KeyValue label="Industry" value={profile.industry} />
                <KeyValue label="Description" value={profile.description} />
                <KeyValue label="Schema version" value={profile.schemaVersion} />
              </dl>
            </ProfileSection>
            {competitors.length > 0 && (
              <ProfileSection title={`Competitors (${competitors.length})`}>
                <ul className="profile-list">
                  {competitors.map((c, i) => (
                    <li key={i}>{typeof c === "string" ? c : (c?.name ?? JSON.stringify(c))}</li>
                  ))}
                </ul>
              </ProfileSection>
            )}
            {valueProps.length > 0 && (
              <ProfileSection title={`Value props (${valueProps.length})`}>
                <ul className="profile-list">
                  {valueProps.map((v, i) => (
                    <li key={i}>{typeof v === "string" ? v : JSON.stringify(v)}</li>
                  ))}
                </ul>
              </ProfileSection>
            )}
            {vocabulary.length > 0 && (
              <ProfileSection title={`Vocabulary (${vocabulary.length})`}>
                <dl className="profile-kv-list">
                  {vocabulary.map((entry, i) => (
                    <KeyValue
                      key={i}
                      label={entry?.term ?? `Term ${i + 1}`}
                      value={entry?.meaning ?? JSON.stringify(entry)}
                    />
                  ))}
                </dl>
              </ProfileSection>
            )}
            {personas.length > 0 && (
              <ProfileSection title={`Target personas (${personas.length})`}>
                <ul className="profile-list">
                  {personas.map((p, i) => (
                    <li key={i}>{typeof p === "string" ? p : (p?.title ?? JSON.stringify(p))}</li>
                  ))}
                </ul>
              </ProfileSection>
            )}
            {(meta.confidence || meta.generated_at || (meta.sources?.length ?? 0) > 0) && (
              <ProfileSection title="Research metadata">
                <dl className="profile-kv-list">
                  <KeyValue label="Confidence" value={meta.confidence} />
                  <KeyValue label="Generated" value={meta.generated_at} />
                  <KeyValue label="Sources" value={meta.sources?.length ?? 0} />
                </dl>
              </ProfileSection>
            )}
            {Object.keys(other).length > 0 && (
              <ProfileSection title="Other">
                <pre className="profile-other">{JSON.stringify(other, null, 2)}</pre>
              </ProfileSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
